import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/Card';
import { Search, RefreshCw } from 'lucide-react';
import { searchEsim, userProfile, getTopupPackages, topup, listPackages } from '../services/api';

export const TopUp: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState<'esim' | 'user'>('esim');
  const [searchResult, setSearchResult] = useState<any | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<string>('');
  const [availablePackages, setAvailablePackages] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string>('');

  // Load available packages when eSIM is selected
  const loadPackages = async (iccid: string) => {
    try {
      const result = await getTopupPackages(iccid);
      if (result && result.packages) {
        setAvailablePackages(result.packages);
      }
    } catch (error) {
      console.error('Error loading packages:', error);
    }
  };

  // Handle direct eSIM selection from user's eSIM list
  const handleSelectEsim = async (esim: any) => {
    setSearchType('esim');
    setSearchTerm(esim.iccid);
    setSearchResult({
      iccid: esim.iccid,
      user_email: esim.customer_email,
      status: esim.status,
      activated_date: esim.activation_date,
      last_topup_date: esim.last_topup_date
    });
    // Load available packages for this eSIM
    await loadPackages(esim.iccid);
    setSelectedPackage('');
    setIsSuccess(false);
    setError('');
  };
  // Search function
  const handleSearch = async () => {
    if (!searchTerm) return;
    setIsLoading(true);
    setSearchResult(null);
    setSelectedPackage('');
    setIsSuccess(false);
    setError('');
    
    try {
      if (searchType === 'esim') {
        // Search for e-sim by ICCID
        const result = await searchEsim(searchTerm);
        if (result && result.data && result.data.length > 0) {
          const esim = result.data[0];
          setSearchResult({
            iccid: esim.iccid,
            user_email: esim.user_email,
            status: esim.status,
            activated_date: esim.activated_date,
            last_topup_date: esim.last_topup_date
          });
          // Load available packages for this eSIM
          await loadPackages(esim.iccid);
        } else {
          setError('E-SIM not found');
        }
      } else {
        // Search for user by email
        const result = await userProfile(searchTerm);
        if (result && result.email) {
          setSearchResult({
            email: result.email,
            name: result.email, // Use email as name since full_name is not in API response
            esims: result.profiles || [] // Use profiles instead of esims
          });
        } else {
          setError('User not found');
        }
      }
    } catch (error) {
      console.error('Search error:', error);
      setError('Search failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  // Handle top-up submission
  const handleTopUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPackage || !searchResult?.iccid) return;
    setIsLoading(true);
    setError('');
    
    try {
      const result = await topup(searchResult.iccid, selectedPackage, 1);
      if (result && result.status === 'ok') {
        setIsSuccess(true);
        // Reset form after success
        setTimeout(() => {
          setSearchTerm('');
          setSearchResult(null);
          setSelectedPackage('');
          setIsSuccess(false);
        }, 3000);
      } else {
        setError('Top-up failed. Please try again.');
      }
    } catch (error) {
      console.error('Top-up error:', error);
      setError('Top-up failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-800">Top-Up E-SIM</h1>
        <p className="mt-1 text-sm text-gray-600">
          Search for an e-sim by ID or user and add a top-up
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Search Section */}
        <div className="md:col-span-1">
          <Card title="Search">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search By
                </label>
                <div className="flex space-x-4">
                  <label className="inline-flex items-center">
                    <input type="radio" className="form-radio text-indigo-600" name="searchType" checked={searchType === 'esim'} onChange={() => setSearchType('esim')} />
                    <span className="ml-2 text-sm text-gray-700">E-SIM ID</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input type="radio" className="form-radio text-indigo-600" name="searchType" checked={searchType === 'user'} onChange={() => setSearchType('user')} />
                    <span className="ml-2 text-sm text-gray-700">
                      User Email
                    </span>
                  </label>
                </div>
              </div>
              <div>
                <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                  {searchType === 'esim' ? 'E-SIM ID' : 'User Email'}
                </label>
                <div className="relative">
                  <input type="text" id="search" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder={searchType === 'esim' ? 'Enter E-SIM ID...' : 'Enter user email...'} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
              </div>
              <button type="button" onClick={handleSearch} disabled={!searchTerm || isLoading} className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed">
                {isLoading ? <>
                    <RefreshCw className="animate-spin h-4 w-4 mr-2" />
                    Searching...
                  </> : 'Search'}
              </button>
              {error && (
                <div className="text-red-600 text-sm mt-2">
                  {error}
                </div>
              )}
            </div>
          </Card>
        </div>
        {/* Results & Top-up Section */}
        <div className="md:col-span-2">
          {isSuccess ? <Card>
              <div className="text-center py-6">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                  <svg className="h-6 w-6 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="mt-3 text-lg font-medium text-gray-900">
                  Top-up Successful
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  The e-sim has been successfully topped up.
                </p>
              </div>
            </Card> : searchResult ? <Card title={searchType === 'esim' ? 'E-SIM Details' : 'User Details'}>
              {searchType === 'esim' ? <div className="space-y-6">
                  {/* E-SIM Details */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        ICCID
                      </p>
                      <p className="mt-1 text-sm text-gray-900">
                        {searchResult.iccid}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">User</p>
                      <p className="mt-1 text-sm text-gray-900">
                        {searchResult.user_email}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Status
                      </p>
                      <p className="mt-1">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${searchResult.status === 'Activated' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {searchResult.status}
                        </span>
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Activation Date
                      </p>
                      <p className="mt-1 text-sm text-gray-900">
                        {searchResult.activated_date || '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Last Top-up Date
                      </p>
                      <p className="mt-1 text-sm text-gray-900">
                        {searchResult.last_topup_date || '-'}
                      </p>
                    </div>
                  </div>
                  {/* Top-up Form */}
                  <form onSubmit={handleTopUp} className="space-y-4 border-t border-gray-200 pt-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      Add Top-up
                    </h3>
                    <div>
                      <label htmlFor="package" className="block text-sm font-medium text-gray-700 mb-1">
                        Select Package
                      </label>
                      <select id="package" value={selectedPackage} onChange={e => setSelectedPackage(e.target.value)} required className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
                        <option value="">Select a package...</option>
                        {availablePackages.map(pkg => <option key={pkg.package_id} value={pkg.package_id}>
                            {pkg.name} - €{pkg.gross_price_eur} ({pkg.data})
                          </option>)}
                      </select>
                    </div>
                    <div>
                      <button type="submit" disabled={!selectedPackage || isLoading} className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed">
                        {isLoading ? <>
                            <RefreshCw className="animate-spin h-4 w-4 mr-2" />
                            Processing...
                          </> : 'Add Top-up'}
                      </button>
                    </div>
                  </form>
                </div> : <div className="space-y-6">
                  {/* User Details */}
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      User Email
                    </p>
                    <p className="mt-1 text-sm text-gray-900">
                      {searchResult.email}
                    </p>
                  </div>
                  {/* E-SIMs List */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">
                      E-SIMs
                    </h3>
                    <div className="space-y-4">
                      {searchResult.esims.map((esim: any, index: number) => <div key={esim.iccid} className={`p-4 rounded-lg ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white border border-gray-200'}`}>
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {esim.iccid}
                              </p>
                              <p className="text-xs text-gray-500">
                                Plan: {esim.plan_title}
                              </p>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${esim.status === 'Activated' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                              {esim.status}
                            </span>
                          </div>
                          <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <p className="text-gray-500">Activation Date</p>
                              <p className="font-medium">
                                {esim.activation_date || '-'}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-500">Last Top-up</p>
                              <p className="font-medium">
                                {esim.last_topup_date || '-'}
                              </p>
                            </div>
                          </div>
                          <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <p className="text-gray-500">Data</p>
                              <p className="font-medium">
                                {esim.plan_data_mb} MB
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-500">Validity</p>
                              <p className="font-medium">
                                {esim.plan_validity_days} days
                              </p>
                            </div>
                          </div>
                          <div className="mt-3">
                            <button type="button" className="text-sm text-indigo-600 hover:text-indigo-900 font-medium" onClick={() => handleSelectEsim(esim)}>
                              Select for Top-up
                            </button>
                          </div>
                        </div>)}
                    </div>
                  </div>
                </div>}
            </Card> : <Card>
              <div className="text-center py-12">
                <Search className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  No E-SIM Selected
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Search for an e-sim by ID or user email to perform a top-up.
                </p>
              </div>
            </Card>}
        </div>
      </div>
    </div>;
};